import React, { useState, useEffect } from 'react';
import { sanitizeText, FIELD_LIMITS } from '../utils/sanitize';

/**
 * Reusable question form component for adding and editing questions.
 * 
 * Props:
 * - initialData: Optional initial question data for editing
 * - onSubmit: Function called with sanitized form data
 * - onCancel: Function called when cancel is clicked (edit mode only)
 * - submitLabel: Label for submit button (default: "Add Question")
 * - isEditing: Boolean indicating edit mode
 */
function QuestionForm({ initialData, onSubmit, onCancel, submitLabel = "Add Question", isEditing = false }) {
  const [formData, setFormData] = useState({
    question: '',
    option_a: '',
    option_b: '',
    option_c: '',
    option_d: '',
    correct_answer: 'A'
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Populate form with initial data when editing
  useEffect(() => {
    if (initialData) {
      setFormData({
        question: initialData.question || '',
        option_a: initialData.option_a || '',
        option_b: initialData.option_b || '',
        option_c: initialData.option_c || '',
        option_d: initialData.option_d || '',
        correct_answer: initialData.correct_answer || 'A'
      });
    }
  }, [initialData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(''); // Clear error on input
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Sanitize all fields
    const sanitized = {
      question: sanitizeText(formData.question, FIELD_LIMITS.question),
      option_a: sanitizeText(formData.option_a, FIELD_LIMITS.option_a),
      option_b: sanitizeText(formData.option_b, FIELD_LIMITS.option_b),
      option_c: sanitizeText(formData.option_c, FIELD_LIMITS.option_c),
      option_d: sanitizeText(formData.option_d, FIELD_LIMITS.option_d),
      correct_answer: formData.correct_answer
    };

    // Validate required fields
    if (!sanitized.question) {
      setError('Question is required');
      return;
    }
    if (!sanitized.option_a || !sanitized.option_b || !sanitized.option_c || !sanitized.option_d) {
      setError('All four options are required');
      return;
    }

    setSubmitting(true);

    try {
      await onSubmit(sanitized);
      
      // Reset form if not editing
      if (!isEditing) {
        setFormData({
          question: '',
          option_a: '',
          option_b: '',
          option_c: '',
          option_d: '',
          correct_answer: 'A'
        });
      }
    } catch (err) {
      setError(err.message || 'Failed to save question');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className={`question-form ${isEditing ? 'editing' : ''}`} onSubmit={handleSubmit}>
      <div className="form-group">
        <label>Question</label>
        <textarea
          name="question"
          value={formData.question}
          onChange={handleInputChange}
          placeholder="Enter your question..."
          maxLength={FIELD_LIMITS.question}
          required
        />
      </div>

      <div className="options-grid">
        <div className="form-group option-group">
          <label className="option-label">
            <span className="option-badge a">A</span>
          </label>
          <input
            type="text"
            name="option_a"
            value={formData.option_a}
            onChange={handleInputChange}
            placeholder="Option A"
            maxLength={FIELD_LIMITS.option_a}
            required
          />
        </div>
        <div className="form-group option-group">
          <label className="option-label">
            <span className="option-badge b">B</span>
          </label>
          <input
            type="text"
            name="option_b"
            value={formData.option_b}
            onChange={handleInputChange}
            placeholder="Option B"
            maxLength={FIELD_LIMITS.option_b}
            required
          />
        </div>
        <div className="form-group option-group">
          <label className="option-label">
            <span className="option-badge c">C</span>
          </label>
          <input
            type="text"
            name="option_c"
            value={formData.option_c}
            onChange={handleInputChange}
            placeholder="Option C"
            maxLength={FIELD_LIMITS.option_c}
            required
          />
        </div>
        <div className="form-group option-group">
          <label className="option-label">
            <span className="option-badge d">D</span>
          </label>
          <input
            type="text"
            name="option_d"
            value={formData.option_d}
            onChange={handleInputChange}
            placeholder="Option D"
            maxLength={FIELD_LIMITS.option_d}
            required
          />
        </div>
      </div>

      <div className="form-group">
        <label>Correct Answer</label>
        <div className="correct-answer-selector">
          {['A', 'B', 'C', 'D'].map(letter => (
            <button
              type="button"
              key={letter}
              className={`answer-btn ${formData.correct_answer === letter ? 'selected' : ''}`}
              onClick={() => setFormData(prev => ({ ...prev, correct_answer: letter }))}
            >
              {letter}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="form-actions">
        {isEditing && onCancel && (
          <button 
            type="button" 
            className="btn btn-secondary"
            onClick={onCancel}
          >
            Cancel
          </button>
        )}
        <button 
          type="submit" 
          className={`btn btn-primary ${!isEditing ? 'btn-full' : ''}`}
          disabled={submitting}
        >
          {submitting ? 'Saving...' : submitLabel}
        </button>
      </div>
    </form>
  );
}

export default QuestionForm;
